import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Typography,
  Divider,
  Tooltip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import InfoIcon from "@mui/icons-material/Info";
import GppGoodOutlinedIcon from "@mui/icons-material/GppGoodOutlined";
import TpvCallButton from "../components/TpvCallButton";
import { getTipById, updateTip } from "../api/tip";
import { getBusinessById } from "../api/business";
import { useNavigate, useParams } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  postCreatePaymentIntent,
  postOneTapPayment,
  postPaymentIntentSaveCard,
  postOneTapToken,
} from "../api/stripe";
import { getTiperPaymentProfile } from "../api/tiper";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { STRIPE_API_KEY, CONSENT_VERSION } from "../utils";
import { KTCookie } from "../utils/cookie";
import { calcularTasaFinal } from "../utils/helpers";
import { event } from "../utils/gtag";
import * as embudo from "../utils/embudo";
import {
  clearTipFormSession,
  isTipAlreadyPaidError,
  isTipPaid,
  leavePaidTipPayment,
} from "../utils/tipPaymentStatus";
import logo from "../assets/logo.png";
import stripeLogo from "../assets/stripe-logo.png";
import BusinessBrandingHeader, {
  getBusinessBrandPrimary,
} from "../components/BusinessBrandingHeader";
import "./StripeScreen.css";

// ✅ Carga la clave pública desde .env
const stripePromise = loadStripe(STRIPE_API_KEY);

// Mapeo de códigos de idioma i18next a locales de Stripe
const getStripeLocale = (i18nLang) => {
  const localeMap = {
    es: "es",
    en: "en",
    ca: "es", // Catalán usa español en Stripe
    fr: "fr",
    de: "de",
    pt: "pt",
    zh: "zh",
    ru: "ru",
    ja: "ja",
    sv: "sv",
    nb: "nb",
    fi: "fi",
  };
  const langCode = i18nLang?.split("-")[0]?.toLowerCase() || "en";
  return localeMap[langCode] || "en";
};

const TIPER_COOKIE_NAME = "tipifyCookie";

const EMPTY_PAYMENT_PROFILE = {
  identified: false,
  saveCardConsent: false,
  canOneTap: false,
  paymentMethods: [],
};

// Nombre comercial de la marca de la tarjeta guardada (Stripe la da en minúsculas)
const CARD_BRAND_LABELS = {
  visa: "Visa",
  mastercard: "Mastercard",
  amex: "American Express",
  discover: "Discover",
  diners: "Diners Club",
  jcb: "JCB",
  unionpay: "UnionPay",
  cartes_bancaires: "Cartes Bancaires",
};
const cardBrandLabel = (brand) =>
  CARD_BRAND_LABELS[brand] ||
  (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : "Tarjeta");

// Componente del formulario de pago
const CheckoutForm = ({
  tipData,
  businessId,
  tipId,
  business,
  brandPrimary = "#66bc5f",
  onSuccess,
  onTaxesChanged,
  // Pago en un toque (aditivo): solo actúa si el tiper está identificado.
  paymentIntentId = null,
  paymentProfile = EMPTY_PAYMENT_PROFILE,
  tiperId = null,
  saveCard = false,
  onSaveCardChange = () => {},
  piSaveCard = false,
  onPiSaveCardChange = () => {},
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState(null);
  // "idle" | "processing" | "failed": si falla, se oculta el botón y queda el PaymentElement
  const [oneTapState, setOneTapState] = useState("idle");
  const [oneTapMessage, setOneTapMessage] = useState(null);
  // Método seleccionado en la persiana de Stripe ("card", "google_pay",
  // "apple_pay", "bizum", "link"...). La casilla de guardar tarjeta solo tiene
  // sentido con Tarjeta: con Google/Apple Pay el cliente ya paga en un gesto y
  // guardar la tarjeta obligaría a su banco a pedir una verificación extra.
  const [selectedMethod, setSelectedMethod] = useState(null);
  const handlePaymentElementChange = (changeEvent) => {
    const type = changeEvent?.value?.type || null;
    if (type && type !== selectedMethod) {
      event({ action: "seleccion_metodo_pago", category: "Pagos", label: type });
    }
    setSelectedMethod(type);
    if (type !== "card" && saveCard) onSaveCardChange(false);
  };
  const [localClickTaxes, setLocalClickTaxes] = useState(
    tipData.clickTaxes || false,
  );
  const [isUpdatingPayment, setIsUpdatingPayment] = useState(false);

  // Sincronizar estado local con tipData cuando cambia
  useEffect(() => {
    setLocalClickTaxes(tipData.clickTaxes || false);
  }, [tipData.clickTaxes]);

  // Calcular importes (usar estado local para clickTaxes)
  let amountCents = 0;
  let amountTasaCents = 0;
  let importePropina = 0;
  // Calcular las tasas siempre para poder mostrarlas tachadas cuando no está activado
  const tasaCalculada = calcularTasaFinal(
    tipData.importe?.import,
    tipData.tasa,
  );
  if (localClickTaxes) {
    amountTasaCents = tasaCalculada;
    importePropina = tipData.importe?.import || 0;
    amountCents = Math.round((importePropina + amountTasaCents) * 100);
    amountTasaCents *= 100;
  } else {
    importePropina = tipData.importe?.import || 0;
    amountCents = Math.round(importePropina * 100);
  }

  const handleToggleTaxes = async () => {
    const newValue = !localClickTaxes;
    setLocalClickTaxes(newValue);
    setIsUpdatingPayment(true);

    // Notificar al componente padre para que actualice el tip y el PaymentIntent
    if (onTaxesChanged) {
      try {
        await onTaxesChanged(newValue);
      } finally {
        setIsUpdatingPayment(false);
      }
    } else {
      setIsUpdatingPayment(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setMessage(null);
    event({
      action: "click_dar_propina",
      category: "Pagos",
      label: "StripeScreen",
      value: tipData.importe?.import || 0,
    });

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setMessage(submitError.message);
        setIsProcessing(false);
        return;
      }

      // Guardar tarjeta (solo tiper identificado): marca el PaymentIntent con
      // customer + setup_future_usage para tarjeta antes de confirmar. Si
      // falla, el pago sigue exactamente igual que sin la casilla.
      const canSaveCard = paymentProfile?.identified && tiperId && paymentIntentId;
      if (canSaveCard && saveCard !== piSaveCard) {
        try {
          await postPaymentIntentSaveCard(paymentIntentId, {
            tiperId,
            save: saveCard,
            consentVersion: CONSENT_VERSION,
          });
          onPiSaveCardChange(saveCard);
        } catch (saveCardError) {
          console.warn(
            "No se pudo marcar el guardado de la tarjeta:",
            saveCardError?.response?.data || saveCardError?.message,
          );
        }
      }

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/${businessId}/success/${tipId}?method=stripe`,
          // Obligatorio al ocultar país y código postal en el PaymentElement:
          // Stripe exige que se pasen ambos valores (el código postal, vacío).
          payment_method_data: {
            billing_details: { address: { country: "ES", postal_code: "" } },
          },
        },
        redirect: "if_required",
      });

      if (error) {
        // El motivo (card_declined, authentication_required…) dice por qué se pierde
        event({
          action: "error_pago",
          category: "Pagos",
          label: `StripeScreen: ${error.decline_code || error.code || error.type || "desconocido"}`,
        });
        embudo.registrar("pago_fallido", { tipId, metodo: `stripe:${error.decline_code || error.code || "error"}` });
        setMessage(error.message);
        setIsProcessing(false);
      } else if (paymentIntent && paymentIntent.status === "succeeded") {
        // Tarjeta guardada: autorizar a este navegador para pagar en un toque
        // (cookie HttpOnly). No bloquea el éxito si falla.
        if (canSaveCard && saveCard) {
          try {
            await postOneTapToken(paymentIntent.id, { tiperId });
          } catch (tokenError) {
            console.warn(
              "No se pudo activar el pago en un toque:",
              tokenError?.response?.data || tokenError?.message,
            );
          }
        }
        // Pago exitoso, limpiar estado y redirigir
        clearTipFormSession();
        onSuccess();
        navigate(`/${businessId}/success/${tipId}?method=stripe`);
      } else {
        setIsProcessing(false);
      }
    } catch (err) {
      console.error("Error al procesar el pago:", err);
      event({ action: "error_pago", category: "Pagos", label: "StripeScreen: excepcion" });
      embudo.registrar("pago_fallido", { tipId, metodo: "stripe:excepcion" });
      setMessage(t("checkout.errorPayment"));
      setIsProcessing(false);
    }
  };

  // ---- Pago en un toque con la tarjeta guardada ----
  const oneTapCard = paymentProfile?.canOneTap
    ? paymentProfile.paymentMethods?.[0] || null
    : null;
  const showOneTap = !!oneTapCard && oneTapState !== "failed";

  // Solo lo usa el pago en un toque
  const goToSuccess = () => {
    event({ action: "pago_un_toque_ok", category: "Pago", label: "StripeScreen" });
    clearTipFormSession();
    onSuccess();
    navigate(`/${businessId}/success/${tipId}?method=stripe`);
  };

  const failOneTap = (reason) => {
    event({ action: "error_pago", category: "Pagos", label: "StripeScreen: un toque" });
    setOneTapState("failed");
    setOneTapMessage(reason || t("oneTap.failed"));
    setIsProcessing(false);
  };

  const handleOneTap = async () => {
    if (!oneTapCard || isProcessing || isUpdatingPayment) return;
    event({ action: "click_pago_un_toque", category: "Pago", label: "StripeScreen" });
    setIsProcessing(true);
    setOneTapState("processing");
    setMessage(null);
    try {
      const res = await postOneTapPayment({
        tipId,
        paymentMethodId: oneTapCard.id,
      });
      const data = res?.data || {};
      if (data.status === "succeeded") {
        goToSuccess();
        return;
      }
      if (data.status === "requires_action" && data.clientSecret && stripe) {
        // 3DS: Stripe abre el reto y confirma el mismo PaymentIntent
        const { error, paymentIntent } = await stripe.confirmCardPayment(
          data.clientSecret,
          { payment_method: data.paymentMethodId || oneTapCard.id },
        );
        if (!error && paymentIntent?.status === "succeeded") {
          goToSuccess();
          return;
        }
        failOneTap(error?.message);
        return;
      }
      failOneTap(data.message);
    } catch (err) {
      if (isTipAlreadyPaidError(err)) {
        leavePaidTipPayment(navigate, businessId);
        return;
      }
      console.warn("Pago en un toque no completado:", err?.response?.data || err?.message);
      failOneTap(err?.response?.data?.message);
    }
  };

  return (
    <form onSubmit={handleSubmit} id="payment-form">
      {/* Flecha de atrás dentro del card */}
      <IconButton
        onClick={() => navigate(-1)}
        sx={{
          position: "absolute",
          left: { xs: 16, sm: 12 },
          top: { xs: 16, sm: 12 },
          color: brandPrimary,
          bgcolor: "transparent",
          zIndex: 10,
          "&:hover": {
            bgcolor: `${brandPrimary}1A`,
          },
        }}
        aria-label={t("checkout.back")}
      >
        <ArrowBackIcon /> {t("checkout.back")}
      </IconButton>
      <Box sx={{ mb: 2, mt: { xs: 7, sm: 5 } }}>
        <BusinessBrandingHeader business={business} />
      </Box>
      <Box sx={{ mb: 2 }}>
        {/* Resumen de precios */}
        <Box sx={{ mb: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              mb: 1,
            }}
          >
            <Typography variant="body1">{t("checkout.yourTip")}</Typography>
            <Typography variant="body1" fontWeight={500}>
              {importePropina.toFixed(2)} €
            </Typography>
          </Box>
          {localClickTaxes && amountTasaCents > 0 ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("checkout.feesOn")}
                </Typography>
                <IconButton
                  onClick={handleToggleTaxes}
                  sx={{
                    p: 0.5,
                    ml: 0.5,
                    "&:hover": {
                      bgcolor: "transparent",
                    },
                  }}
                  aria-label={t("checkout.feesOnAria")}
                >
                  <span
                    className="heartbeat"
                    style={{ fontSize: "16px", cursor: "pointer" }}
                  >
                    💚
                  </span>
                </IconButton>
                <Tooltip
                  title={t("checkout.feesInfo")}
                  arrow
                  placement="top"
                >
                  <InfoIcon
                    sx={{
                      fontSize: "16px",
                      color: "text.secondary",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                style={{ paddingTop: "6px" }}
              >
                {tasaCalculada.toFixed(2)} €
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                mb: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {t("checkout.feesOff")}
                </Typography>
                <IconButton
                  onClick={handleToggleTaxes}
                  sx={{
                    p: 0.5,
                    ml: 0.5,
                    "&:hover": {
                      bgcolor: "transparent",
                    },
                  }}
                  aria-label={t("checkout.feesOffAria")}
                >
                  <span style={{ fontSize: "16px", cursor: "pointer" }}>
                    👉🤍
                  </span>
                </IconButton>
                <Tooltip
                  title={t("checkout.feesInfo")}
                  arrow
                  placement="top"
                >
                  <InfoIcon
                    sx={{
                      fontSize: "16px",
                      color: "text.secondary",
                      cursor: "help",
                    }}
                  />
                </Tooltip>
              </Box>
              <Typography
                variant="body2"
                color="text.secondary"
                style={{
                  paddingTop: "6px",
                  textDecoration: localClickTaxes ? "none" : "line-through",
                  opacity: localClickTaxes ? 1 : 0.6,
                }}
              >
                {tasaCalculada.toFixed(2)} €
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6" fontWeight={500} sx={{ color: brandPrimary }}>
              {t("checkout.total")}
            </Typography>
            <Typography variant="h6" fontWeight={500} sx={{ color: brandPrimary }}>
              {(amountCents / 100).toFixed(2)} €
            </Typography>
          </Box>
        </Box>
        {showOneTap && (
          <Box sx={{ mb: 2 }}>
            <Button
              type="button"
              variant="contained"
              fullWidth
              size="large"
              startIcon={<CreditCardIcon />}
              disabled={isProcessing || isUpdatingPayment}
              onClick={handleOneTap}
              sx={{
                py: 1.5,
                fontSize: "1.1rem",
                fontWeight: 600,
                textTransform: "none",
                borderRadius: "30px",
                bgcolor: brandPrimary,
                "&:hover": { bgcolor: brandPrimary, filter: "brightness(0.92)" },
              }}
            >
              {oneTapState === "processing"
                ? t("oneTap.processing")
                : t("oneTap.payWithCard", {
                    brand: cardBrandLabel(oneTapCard.brand),
                    last4: oneTapCard.last4,
                    amount: (amountCents / 100).toFixed(2),
                  })}
            </Button>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", textAlign: "center", mt: 1 }}
            >
              {t("oneTap.orOther")}
            </Typography>
          </Box>
        )}
        {oneTapState === "failed" && oneTapMessage && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1,
              bgcolor: "#fff8e1",
              border: "1px solid #ffe082",
            }}
          >
            <Typography variant="body2" color="text.secondary">
              {oneTapMessage}
            </Typography>
          </Box>
        )}
        <Box sx={{ position: "relative" }}>
          <Box
            className="stripe-trust-badge"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 0.75,
              mb: 1.5,
              px: 1,
              py: 0.75,
              borderRadius: 1,
              bgcolor: "rgba(10, 37, 64, 0.04)",
              border: "1px solid rgba(10, 37, 64, 0.08)",
            }}
          >
            <GppGoodOutlinedIcon
              sx={{ color: brandPrimary, fontSize: 20, flexShrink: 0 }}
            />
            <Typography
              variant="caption"
              sx={{
                color: "text.secondary",
                fontSize: "12px",
                lineHeight: 1.3,
                textAlign: "center",
              }}
            >
              {t("payment.secureByStripe")}
            </Typography>
            <Box
              component="img"
              src={stripeLogo}
              alt="Stripe"
              sx={{
                height: 16,
                width: "auto",
                display: "block",
                borderRadius: "3px",
                flexShrink: 0,
              }}
            />
          </Box>
          {isUpdatingPayment && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(255, 255, 255, 0.9)",
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {t("checkout.updating")}
              </Typography>
            </Box>
          )}
          <PaymentElement
            options={{
              // Apple Pay / Google Pay primero y seleccionados por defecto
              // cuando el móvil los tiene (el método más rápido); después la
              // tarjeta y Bizum.
              layout: {
                type: "accordion",
                defaultCollapsed: false,
                radios: false,
                spacedAccordionItems: false,
              },
              // Sin Stripe Link: añadía dentro de Tarjeta un bloque opcional con
              // email, móvil y nombre que alargaba el pago. Para guardar la
              // tarjeta ya está nuestra casilla (pago en un toque).
              // Apple Pay y Google Pay NO cambian.
              paymentMethodTypes: ["card", "bizum"],
              paymentMethodOrder: ["apple_pay", "google_pay", "card", "bizum"],
              wallets: {
                applePay: "auto",
                googlePay: "auto",
                link: "never",
              },
              // País fijo (España) y sin código postal: para una propina no
              // aportan nada y el selector salía con "Estados Unidos" por defecto.
              // Al ocultarlos hay que enviar el país en confirmPayment.
              defaultValues: {
                billingDetails: { address: { country: "ES" } },
              },
              fields: {
                billingDetails: {
                  address: { country: "never", postalCode: "never" },
                },
              },
            }}
            onChange={handlePaymentElementChange}
          />
        </Box>
        {paymentProfile?.identified && tiperId && selectedMethod === "card" && (
          // Tarjeta seleccionable con el mismo aspecto que las filas de la
          // persiana de Stripe: borde fino, esquinas de 8 px, texto a la izquierda.
          <Box
            component="label"
            sx={{
              mt: 1.5,
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: "12px 14px",
              border: "1px solid",
              borderColor: saveCard ? brandPrimary : "#e6e6e6",
              borderRadius: "8px",
              bgcolor: saveCard ? `${brandPrimary}12` : "#fff",
              textAlign: "left",
              cursor: isProcessing || isUpdatingPayment ? "default" : "pointer",
              transition: "border-color 0.15s, background-color 0.15s",
            }}
          >
            <Checkbox
              checked={saveCard}
              onChange={(e) => onSaveCardChange(e.target.checked)}
              disabled={isProcessing || isUpdatingPayment}
              size="small"
              sx={{
                p: 0,
                mt: "1px",
                color: "#b5b9c4",
                "&.Mui-checked": { color: brandPrimary },
              }}
            />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: "0.9rem", fontWeight: 600, color: "#32325d", lineHeight: 1.3 }}
              >
                {t("oneTap.saveCard")}
              </Typography>
              <Typography
                sx={{ fontSize: "0.78rem", color: "text.secondary", lineHeight: 1.4, mt: 0.25 }}
              >
                {t("oneTap.saveCardHint")}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>

      {message && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            bgcolor: "error.light",
            color: "error.contrastText",
            borderRadius: 1,
          }}
        >
          <Typography variant="body2">{message}</Typography>
        </Box>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        disabled={!stripe || isProcessing || isUpdatingPayment}
        sx={{
          mt: 2,
          mb: 2,
          py: 1.5,
          fontSize: "1.5rem",
          fontWeight: 500,
          textTransform: "none",
          bgcolor: brandPrimary,
          "&:hover": {
            bgcolor: brandPrimary,
            filter: "brightness(0.92)",
          },
        }}
      >
        {isProcessing
          ? t("checkout.processing")
          : t("checkout.confirm", { amount: (amountCents / 100).toFixed(2) })}
      </Button>
      {business?.tpvLlamadaActiva && (
        <TpvCallButton
          fullWidth
          accent={brandPrimary}
          disabled={isProcessing || isUpdatingPayment}
          onClick={(ubicacion) =>
            navigate(
              `/${businessId}/payout/${tipId}?method=tpv`,
              ubicacion ? { state: { tpvUbicacion: ubicacion } } : undefined,
            )
          }
        />
      )}
    </form>
  );
};

const StripeScreen = () => {
  const { id: businessId, idtip: tipId } = useParams();
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentIntentId, setPaymentIntentId] = useState(null);
  const [tipData, setTipData] = useState(null);
  const [business, setBusiness] = useState(null);
  const [loading, setLoading] = useState(true);
  // Pago en un toque: tiper de la cookie y su perfil de pago (identificado,
  // consentimiento, tarjetas guardadas). Sin tiper identificado no cambia nada.
  const [tiperId, setTiperId] = useState(null);
  const [paymentProfile, setPaymentProfile] = useState(EMPTY_PAYMENT_PROFILE);
  const [saveCard, setSaveCard] = useState(false); // casilla, desmarcada por defecto
  const [piSaveCard, setPiSaveCard] = useState(false); // lo que tiene el PaymentIntent
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    event({
      action: "carga_pantalla_pago",
      category: "Pantallas",
      label: "StripeScreen",
    });
  }, []);

  const createPaymentIntent = async (
    tip,
    shouldIncludeTaxes,
    { saveCard: wantSaveCard = false, tiperId: tiperIdForSave = null } = {},
  ) => {
    // Determinar importe y negocio destino
    let amountCents = 0;
    let amountTasaCents = 0;
    if (shouldIncludeTaxes) {
      amountTasaCents = calcularTasaFinal(tip.importe?.import, tip.tasa);
      amountCents = Math.round(
        (tip.importe?.import + amountTasaCents || 0) * 100,
      );
      amountTasaCents *= 100;
    } else {
      amountCents = Math.round((tip.importe?.import || 0) * 100);
    }

    let businessAccountId = null;
    if (typeof tip.business === "string") {
      // si solo es un ID, busca el negocio
      const businessRes = await getBusinessById(tip.business);
      businessAccountId =
        businessRes.data?.onboarding?.stripe?.id || businessRes.data?._id;
    } else {
      // si viene populado
      businessAccountId =
        tip.business?.onboarding?.stripe?.id || tip.business?._id;
    }

    if (!amountCents || !businessAccountId) {
      throw new Error("Faltan datos para crear la sesión de pago.");
    }

    // Crear PaymentIntent en backend. tiperId/saveCard solo cuando el tiper
    // identificado ya ha marcado la casilla (p. ej. al recrear el intent tras
    // cambiar los costes de gestión); sin ellos la petición es la de siempre.
    const res = await postCreatePaymentIntent({
      amountCents,
      amountTasaCents,
      businessAccountId,
      tipId,
      ...(wantSaveCard && tiperIdForSave
        ? { tiperId: tiperIdForSave, saveCard: true }
        : {}),
    });

    if (res.data?.clientSecret) {
      return {
        clientSecret: res.data.clientSecret,
        paymentIntentId: res.data.paymentIntentId || null,
        saveCard: !!res.data.saveCard,
      };
    } else {
      throw new Error("Respuesta inesperada del servidor Stripe.");
    }
  };

  // Asegurar que el body tenga fondo blanco
  useEffect(() => {
    // Guardar el estilo original del body
    const originalBodyStyle = {
      backgroundColor: document.body.style.backgroundColor,
      background: document.body.style.background,
    };

    // Aplicar fondo blanco
    document.body.style.backgroundColor = "#ffffff";
    document.body.style.background = "#ffffff";

    // Limpiar al desmontar
    return () => {
      document.body.style.backgroundColor = originalBodyStyle.backgroundColor;
      document.body.style.background = originalBodyStyle.background;
    };
  }, []);

  useEffect(() => {
    const loadTip = async () => {
      try {
        setLoading(true);
        // 1️⃣ Obtener datos de la propina
        const tipRes = await getTipById(tipId);
        const tip = tipRes.data;

        if (!tip) throw new Error("No se pudo obtener la propina.");

        if (isTipPaid(tip.estadoPago)) {
          leavePaidTipPayment(navigate, businessId);
          return;
        }

        setTipData(tip);

        // Cargar business completo para branding (logo, estilos, dirección)
        const businessIdToLoad =
          typeof tip.business === "string"
            ? tip.business
            : tip.business?._id || tip.business;
        if (businessIdToLoad) {
          try {
            const businessRes = await getBusinessById(businessIdToLoad);
            if (businessRes?.data) {
              setBusiness(businessRes.data);
            } else if (typeof tip.business === "object") {
              setBusiness(tip.business);
            }
          } catch (bizErr) {
            console.warn("No se pudo cargar business para branding:", bizErr);
            if (typeof tip.business === "object") {
              setBusiness(tip.business);
            }
          }
        }

        // Perfil de pago del tiper de la cookie (pago en un toque). Nunca
        // bloquea el pago: si falla, se sigue como tiper anónimo.
        const cookieTiperId = KTCookie.getCookie(TIPER_COOKIE_NAME) || null;
        setTiperId(cookieTiperId);
        if (cookieTiperId) {
          try {
            const profileRes = await getTiperPaymentProfile({
              tiperId: cookieTiperId,
            });
            setPaymentProfile({ ...EMPTY_PAYMENT_PROFILE, ...(profileRes?.data || {}) });
          } catch (profileErr) {
            console.warn("No se pudo cargar el perfil de pago del tiper:", profileErr);
            setPaymentProfile(EMPTY_PAYMENT_PROFILE);
          }
        }

        // 2️⃣ Crear PaymentIntent
        const intent = await createPaymentIntent(tip, tip.clickTaxes || false);
        setClientSecret(intent.clientSecret);
        setPaymentIntentId(intent.paymentIntentId);
        setPiSaveCard(intent.saveCard);
      } catch (err) {
        if (isTipAlreadyPaidError(err)) {
          leavePaidTipPayment(navigate, businessId);
          return;
        }
        console.error("❌ Error al iniciar pago Stripe:", err);
        event({ action: "error_pago", category: "Pagos", label: "StripeScreen: inicio" });
        Swal.fire({
          icon: "error",
          title: t("checkout.errorTipTitle"),
          text: t("checkout.errorSession"),
        });
        navigate(`/${businessId}/payout/${tipId}`);
      } finally {
        setLoading(false);
      }
    };

    loadTip();
  }, [tipId, businessId, navigate]);

  const handleTaxesChanged = async (newClickTaxes) => {
    if (!tipData) return;

    try {
      // Actualizar el tip en el backend
      await updateTip(tipId, { clickTaxes: newClickTaxes });

      // Actualizar tipData localmente
      const updatedTipData = { ...tipData, clickTaxes: newClickTaxes };
      setTipData(updatedTipData);

      // Recrear PaymentIntent con el nuevo amount (conservando la casilla de
      // guardar tarjeta si el tiper identificado la había marcado)
      const intent = await createPaymentIntent(updatedTipData, newClickTaxes, {
        saveCard: paymentProfile?.identified && saveCard,
        tiperId,
      });
      setClientSecret(intent.clientSecret);
      setPaymentIntentId(intent.paymentIntentId);
      setPiSaveCard(intent.saveCard);
    } catch (err) {
      if (isTipAlreadyPaidError(err)) {
        leavePaidTipPayment(navigate, businessId);
        return;
      }
      console.error("Error al actualizar taxes:", err);
      Swal.fire({
        icon: "error",
        title: t("common.error"),
        text: t("checkout.errorFees"),
      });
      throw err; // Re-lanzar para que el componente hijo pueda manejar el error
    }
  };

  // 🌀 Loader mientras se crea el PaymentIntent
  if (loading || !clientSecret || !tipData) {
    return (
      <div className="loading-container">
        <img
          src={logo}
          alt="Tipify Logo"
          className="loading-logo pulse-effect"
        />
      </div>
    );
  }

  const stripeLocale = getStripeLocale(i18n.language);
  const brandPrimary = getBusinessBrandPrimary(business);

  // 💳 Render Stripe Elements con PaymentElement
  return (
    <Box
      component="main"
      sx={{
        minHeight: "100vh",
        bgcolor: "#ffffff",
        width: "100%",
        margin: 0,
        padding: 0,
      }}
    >
      {/* Card blanco del checkout */}
      <Box
        sx={{
          bgcolor: "#ffffff",
          borderRadius: 0,
          p: { xs: 3, sm: 4, md: 5 },
          width: "100%",
          minHeight: "100vh",
          position: "relative",
        }}
      >
        <Elements
          key={clientSecret} // Forzar reinicialización cuando cambia el clientSecret
          stripe={stripePromise}
          options={{
            clientSecret,
            locale: stripeLocale,
            appearance: {
              theme: "stripe",
              variables: {
                colorPrimary: brandPrimary,
                colorBackground: "#ffffff",
                colorText: "#32325d",
                colorDanger: "#df1b41",
                colorSuccess: brandPrimary,
                fontFamily:
                  'system-ui, "Helvetica Neue", Helvetica, sans-serif',
                spacingUnit: "4px",
                borderRadius: "8px",
              },
            },
          }}
        >
          <CheckoutForm
            tipData={tipData}
            businessId={businessId}
            tipId={tipId}
            business={business}
            brandPrimary={brandPrimary}
            onSuccess={() => {
              console.log("Pago completado exitosamente");
            }}
            onTaxesChanged={handleTaxesChanged}
            paymentIntentId={paymentIntentId}
            paymentProfile={paymentProfile}
            tiperId={tiperId}
            saveCard={saveCard}
            onSaveCardChange={setSaveCard}
            piSaveCard={piSaveCard}
            onPiSaveCardChange={setPiSaveCard}
          />
        </Elements>
      </Box>
    </Box>
  );
};

export default StripeScreen;
